import React, { useState, Suspense, lazy, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../context/PermissionsContext';
import AdminPage from '../../../components/admin/AdminPage';

// Lazy load components
const AllCourses = lazy(() => import('./AllCourses'));
const Approved = lazy(() => import('./Approved'));
const Pending = lazy(() => import('./Pending'));
const WaitingForApproval = lazy(() => import('./WaitingForApproval'));
const Rejected = lazy(() => import('./Rejected'));
const CourseCategories = lazy(() => import('./categories/CourseCategories'));
const AllCertificates = lazy(() => import('../certificates/AllCertificates'));

const TAB_IDS = ['all', 'approved', 'pending', 'waiting', 'rejected', 'categories', 'certificates'];

function Courses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(TAB_IDS.includes(tabParam) ? tabParam : 'all');

  useEffect(() => {
    if (tabParam && TAB_IDS.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const setActiveTabAndUrl = (id) => {
    setActiveTab(id);
    setSearchParams(id === 'all' ? {} : { tab: id });
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const { permissions } = usePermissions();
  const navigate = useNavigate();

  // Loading fallback component
  const LoadingFallback = () => (
    <div className="text-center p-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  const handleCreateCourse = () => {
    if (!permissions.course_management_create) {
      return;
    }
    setCreateLoading(true);
    navigate('/admin/courses/create');
  };

  const showCreateCourse = activeTab !== 'certificates' && permissions.course_management_create;

  return (
    <AdminPage
      title="Courses"
      description="Manage and review all courses. Create new courses or edit modules and content."
      actions={
        showCreateCourse ? (
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#7083FF] hover:bg-[#5f73e6] text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            onClick={handleCreateCourse}
            disabled={createLoading}
            title="Create a new course"
          >
            {createLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Icon icon="fluent:add-24-regular" width="18" height="18" />
                <span>Create Course</span>
              </>
            )}
          </button>
        ) : null
      }
    >
      <div className="w-full">
        {/* Single row: tabs + search */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { id: 'all', label: 'All Courses', icon: 'fluent:library-24-regular' },
              { id: 'approved', label: 'Approved', icon: 'fluent:checkmark-circle-24-regular' },
              { id: 'pending', label: 'Pending', icon: 'fluent:clock-24-regular' },
              { id: 'waiting', label: 'Waiting for Approval', icon: 'fluent:timer-24-regular' },
              { id: 'rejected', label: 'Delisted', icon: 'fluent:dismiss-circle-24-regular' },
              { id: 'categories', label: 'Manage category', icon: 'fluent:folder-24-regular' },
              { id: 'certificates', label: 'Certificates', icon: 'fluent:certificate-24-regular' },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTabAndUrl(tab.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors shrink-0 ${
                    isActive
                      ? 'bg-[#7083FF] text-white border-[#7083FF] shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon icon={tab.icon} width={18} height={18} className="shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          {['all', 'approved', 'pending', 'waiting', 'rejected'].includes(activeTab) && (
            <div className="relative flex-1 min-w-[200px] max-w-sm ml-auto">
              <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="search"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Tab content */}
        <div className="min-h-[360px] pt-3 sm:pt-4">
          {[
            { id: 'all', component: AllCourses },
            { id: 'approved', component: Approved },
            { id: 'pending', component: Pending },
            { id: 'waiting', component: WaitingForApproval },
            { id: 'rejected', component: Rejected },
            { id: 'categories', component: CourseCategories },
            { id: 'certificates', component: AllCertificates },
          ].map((tab) => (
            <div
              key={tab.id}
              className={activeTab === tab.id ? 'block' : 'hidden'}
              // style={activeTab === tab.id ? { animation: 'coursesTabFade 0.3s ease-out' } : undefined}
            >
              <Suspense fallback={<LoadingFallback />}>
                {['all', 'approved', 'pending', 'waiting', 'rejected'].includes(tab.id) ? (
                  <tab.component key={activeTab} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                ) : (
                  <tab.component key={activeTab} />
                )}
              </Suspense>
            </div>
          ))}
        </div>

        <style>{`
          @keyframes coursesTabFade {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </AdminPage>
  );
}

export default Courses;